import { type FC } from "react";

import { Mail } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const FULL_NAME_KEY = "admin_someone_else_full_name";
const RELATIONSHIP_KEY = "admin_someone_else_relationship";
const EMAIL_KEY = "admin_someone_else_email";
const PHONE_KEY = "admin_someone_else_phone";
const NOTE_KEY = "admin_someone_else_note";

const RELATIONSHIP_OPTIONS = [
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "grandchild", label: "Grandchild" },
  { value: "niece-nephew", label: "Niece/Nephew" },
  { value: "other-family", label: "Other family" },
] as const;

export const AdminSomeoneElseScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { person, user } = useAppContext();
  const decedentName = person?.first_name ?? "the decedent";
  const userName = user?.full_name ?? user?.first_name ?? "You";

  const inviteeName = ((answers[FULL_NAME_KEY] as string) ?? "").trim() || "{Name}";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          The person applying files the forms
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          The court forms are filed by the person asking to serve. Invite {inviteeName} to
          Elayne so they can pick up from here, and you can help along the way.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Full name</Label>
          <Input
            value={(answers[FULL_NAME_KEY] as string) ?? ""}
            onChange={(e) => setAnswer(FULL_NAME_KEY, e.target.value)}
            placeholder="Full name"
          />
          <FieldError message={errors?.someone_full_name} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Relationship to {decedentName}</Label>
          <Select
            value={(answers[RELATIONSHIP_KEY] as string) ?? ""}
            onValueChange={(v) => setAnswer(RELATIONSHIP_KEY, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a relationship" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors?.someone_relationship} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Email</Label>
          <Input
            type="email"
            value={(answers[EMAIL_KEY] as string) ?? ""}
            onChange={(e) => setAnswer(EMAIL_KEY, e.target.value)}
            placeholder="example@email.com"
          />
          <FieldError message={errors?.someone_email} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>
            Phone <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            type="tel"
            value={(answers[PHONE_KEY] as string) ?? ""}
            onChange={(e) => setAnswer(PHONE_KEY, e.target.value)}
            placeholder="123-456-7890"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-primary">
        <div className="flex items-center gap-2 bg-subtle px-4 py-3">
          <Mail className="size-4 text-secondary" />
          <p className="text-sm text-secondary">Here's the invitation we'll send</p>
        </div>
        <div className="flex flex-col gap-3 bg-white p-4 text-sm">
          <p>
            {userName} invited you to {decedentName}'s estate on Elayne.
          </p>
          <p className="text-secondary">
            Elayne is an estate settlement platform built for families and attorneys,
            supporting them with everything from organization, to probate, to finding
            and transferring the estate's assets.
          </p>
          <p className="text-secondary">
            The account is already set up, documents and details are in place, and the
            next steps are waiting on you. Your authority is what moves the estate forward.
          </p>
          <p className="text-sm font-medium text-brand underline">
            Join {decedentName}'s estate
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>
          Add a personal note <span className="text-tertiary">(optional)</span>
        </Label>
        <Input
          value={(answers[NOTE_KEY] as string) ?? ""}
          onChange={(e) => setAnswer(NOTE_KEY, e.target.value)}
          placeholder="Enter personal note"
        />
      </div>
    </div>
  );
};
